import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Define types for user data
export interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileType: string;
  isAdmin: boolean;
  status: string;
  isApproved: boolean;
  avatar_url?: string;
  profession?: string;
  location?: string;
  education?: string;
  qualificationDocumentUrl?: string;
}

// Sign up a new user
export const signUp = async (
  email: string,
  password: string,
  userData: Omit<UserData, 'id' | 'status' | 'isAdmin' | 'isApproved'>
) => {
  try {
    // Register the user with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm`,
        data: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          profileType: userData.profileType,
          profession: userData.profession || '',
          location: userData.location || '',
          education: userData.education || '',
          
        }
      }
    });

    if (authError) throw authError;
    
    if (authData.user) {
      return {
        success: true,
        message: 'Your account is pending approval.',
        user: authData.user,
      };
    }
    
    return {
      success: false,
      message: 'Something went wrong during sign up.',
    };
  } catch (error: any) {
    console.error('Sign up error:', error);
    return {
      success: false,
      message: error.message || 'Failed to create account.',
    };
  }
};

// Sign in a user
export const signIn = async (email: string, password: string) => {
  try {
    // Check if account is locked
    const { data: lockedData } = await supabase
      .from('account_locks')
      .select('*')
      .eq('email', email)
      .single();
    
    if (lockedData && lockedData.locked) {
      return {
        success: false,
        message: 'Account locked due to too many failed login attempts. Please contact support.',
      };
    }

    // Attempt to sign in
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) {
      // Increment failed attempts
      await incrementFailedAttempts(email);
      
      return {
        success: false,
        message: 'Invalid email or password.',
      };
    }

    if (authData.user) {
      // Get user profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .single();
      
      if (profileError) throw profileError;

      // Reset failed attempts on successful login
      await resetFailedAttempts(email);
      
      // Check if account is approved
      if (!profileData.is_approved) {
        return {
          success: false,
          message: 'Your account is still under review. We\'ll notify you once it\'s approved.',
        };
      }
      
      if (profileData.status === 'Inactive') {
        return {
          success: false,
          message: 'Your account has been deactivated. Please contact support.',
        };
      }

      return {
        success: true,
        user: {
          id: profileData.id,
          email: profileData.email,
          firstName: profileData.first_name,
          lastName: profileData.last_name,
          profileType: profileData.profile_type,
          isAdmin: profileData.is_admin,
          status: profileData.status,
          isApproved: profileData.is_approved,
        },
      };
    }
    
    return {
      success: false,
      message: 'Something went wrong during sign in.',
    };
  } catch (error: any) {
    console.error('Sign in error:', error);
    return {
      success: false,
      message: error.message || 'Failed to sign in.',
    };
  }
};

// Sign out the current user
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Sign out error:', error);
    return {
      success: false,
      message: error.message || 'Failed to sign out.',
    };
  }
};

// Get the current user
export const getCurrentUser = async (): Promise<UserData | null> => {
  try {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    
    if (authData.user) {
      // Check if email is verified
      if (!authData.user.email_confirmed_at) {
        console.log('User email not verified:', authData.user.email);
        return null; // Return null for unverified users
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          first_name,
          last_name,
          profile_type,
          is_admin,
          status,
          is_approved,
          avatar_url
        `)
        .eq('id', authData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      return {
        id: profileData.id,
        email: profileData.email,
        firstName: profileData.first_name,
        lastName: profileData.last_name,
        profileType: profileData.profile_type,
        isAdmin: profileData.is_admin,
        status: profileData.status,
        isApproved: profileData.is_approved,
        avatar_url: profileData.avatar_url
      };
    }
    
    return null;
  } catch (error: any) {
    console.error('Get current user error:', error);
    return null;
  }
};

// Check if an account is locked
export const isAccountLocked = async (email: string): Promise<boolean> => {
  try {
    const { data } = await supabase
      .from('account_locks')
      .select('locked')
      .eq('email', email)
      .single();
    
    return data?.locked || false;
  } catch (error) {
    console.error('Error checking account lock:', error);
    return false;
  }
};

// Unlock a user account
export const unlockUserAccount = async (email: string) => {
  try {
    const { error } = await supabase
      .from('account_locks')
      .update({ locked: false, attempts: 0 })
      .eq('email', email);
    
    if (error) throw error;
    
    return { success: true };
  } catch (error: any) {
    console.error('Unlock account error:', error);
    return {
      success: false,
      message: error.message || 'Failed to unlock account.',
    };
  }
};

// Increment failed login attempts
const incrementFailedAttempts = async (email: string) => {
  try {
    const { data } = await supabase
      .from('account_locks')
      .select('*')
      .eq('email', email)
      .single();
    
    if (!data) {
      // Create new record
      await supabase
        .from('account_locks')
        .insert({
          email,
          attempts: 1,
          locked: false,
          last_attempt: new Date().toISOString()
        });
    } else {
      // Update existing record
      const newAttempts = (data.attempts || 0) + 1;
      const shouldLock = newAttempts >= 5;
      
      await supabase
        .from('account_locks')
        .update({
          attempts: newAttempts,
          locked: shouldLock,
          last_attempt: new Date().toISOString()
        })
        .eq('email', email);
    }
  } catch (error) {
    console.error('Failed to increment attempts:', error);
  }
};

// Reset failed login attempts
const resetFailedAttempts = async (email: string) => {
  try {
    await supabase
      .from('account_locks')
      .update({ attempts: 0, locked: false })
      .eq('email', email);
  } catch (error) {
    console.error('Failed to reset attempts:', error);
  }
};

// Admin functions

// Approve a user account
export const approveAccount = async (userId: string) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        status: 'Active',
        is_approved: true,
      })
      .eq('id', userId);
    
    if (error) throw error;
    
    return { success: true };
  } catch (error: any) {
    console.error('Approve account error:', error);
    return {
      success: false,
      message: error.message || 'Failed to approve account.',
    };
  }
};

// Reject a user account
export const rejectAccount = async (userId: string) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId);
    
    if (error) throw error;
    
    return { success: true };
  } catch (error: any) {
    console.error('Reject account error:', error);
    return {
      success: false,
      message: error.message || 'Failed to reject account.',
    };
  }
};

// Update user status
export const updateUserStatus = async (userId: string, status: string) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ status })
      .eq('id', userId);
    
    if (error) throw error;
    
    return { success: true };
  } catch (error: any) {
    console.error('Update user status error:', error);
    return {
      success: false,
      message: error.message || 'Failed to update user status.',
    };
  }
};

// Remove user
export const removeUser = async (userId: string) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId);
    
    if (error) throw error;
    
    return { success: true };
  } catch (error: any) {
    console.error('Remove user error:', error);
    return {
      success: false,
      message: error.message || 'Failed to remove user.',
    };
  }
};